import { ProjectBatchOperationError } from './project-batch-operation-error'
import { MAX_BATCH_OPERATION_ITEMS } from '@awork/_shared/constants/batch-operation.constants'
import type { Observable } from 'rxjs'
import {
  bufferCount,
  concatMap,
  from,
  catchError,
  concat,
  EMPTY,
  filter,
  forkJoin,
  map,
  merge,
  mergeMap,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
  toArray
} from 'rxjs'
import type { Signal } from '@angular/core'
import { Injectable, Injector, inject } from '@angular/core'
import type { MultiStatusResponse, Options, QueryParams } from '@awork/_shared/services/api-client/ApiClient'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import type { ApiCacheOptions } from '@awork/_shared/services/api-cache-service/types'
import { ApiCacheService } from '@awork/_shared/services/api-cache-service/api-cache.service'
import { apiEndpoint } from '@awork/environments/environment'
import type {
  AcceptedProjectConnection,
  Autopilot,
  AutopilotAlert,
  AutopilotType,
  ProjectConnectionInformation,
  ProjectConnectionInvite,
  ProjectConnectionStatus,
  TrustedProjectConnection,
  TrustedProjectConnectionForm
} from '@awork/features/project/models/project.model'
import { Project } from '@awork/features/project/models/project.model'
import { getLatestSyncDate } from '@awork/core/state/signal-store/helpers'
import { SharedRequest } from '@awork/core/state/signal-store/sharedRequest'
import { ProjectTemplate } from '@awork/features/project/models/project-template.model'
import { ProjectType } from '@awork/features/project/models/project-type.model'
import { ProjectRole } from '@awork/features/project/models/project-role.model'
import { ProjectStatus } from '@awork/features/project/models/project-status.model'
import { ProjectMember } from '@awork/features/project/models/project-member.model'
import type { HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { HttpParams } from '@angular/common/http'
import type { TaskBundle } from '@awork/features/task/models/task-bundle.model'
import type { TaskBundleAssignment } from '@awork/features/task/models/task-bundle-assignment.model'
import { ProjectQuery, ProjectColumn } from '@awork/features/project/state/project.query'
import type { QuickFilter } from '@awork/features/project/types/project-page-settings'
import { ProjectStore } from '@awork/features/project/state/project.store'
import { ProjectTypeStore } from '@awork/features/project/state/project-type.store'
import { UserQuery } from '@awork/features/user/state/user.query'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import { arrayAdd, arrayRemove, arrayUpdate } from '@awork/core/state/signal-store/helpers'
import { trimRichText } from '@awork/_shared/functions/string-operations'
import { ProjectMilestone } from '@awork/features/project/models/project-milestone.model'
import { generateGUID } from '@awork/_shared/functions/guid-generator'
import { TimeTrackingStore } from '@awork/features/time-tracking/state/time-tracking.store'
import type { Tag, TagOwner } from '@awork/_shared/models/tag.model'
import { PermissionsService } from '@awork/features/workspace/services/permission-service/permissions.service'
import { AccessLevels, Features } from '@awork/features/workspace/models/permissions.model'
import { NavigationEnd, Router } from '@angular/router'
import { format } from '@awork/_shared/functions/date-fns-wrappers'
import { FileService } from '@awork/_shared/services/file-service/file.service'
import type { FileUpload } from '@awork/_shared/models/file-upload.model'
import { uniqueBy } from '@awork/_shared/functions/array-operations'
import { formatDateForFilter } from '@awork/_shared/functions/query-params-helper'
import type { DateRange } from '@awork/_shared/functions/date-operations'
import { dateToISO, extractDateOnlyISOString } from '@awork/_shared/functions/date-operations'
import { PublicProjectTemplate } from '@awork/features/project/models/public-project-template.model'
import { groupBy } from '@awork/_shared/functions/lodash'
import { TaskStore } from '@awork/features/task/state/task.store'
import { TimeBookingStore } from '@awork/features/planner/state/time-booking.store'
import { Workspace } from '@awork/features/workspace/models/workspace.model'
import { User } from '@awork/features//user/models/user.model'
import type { BlobFile } from '@awork/_shared/services/file-service/types'
import type {
  CopyProjectForm,
  CopyProjectResponse,
  IDeleteProjectRoleBody,
  IDeleteProjectStatusBody,
  IDeleteProjectTypeBody,
  ProjectBatchRequestBody,
  IProjectDeleteBody,
  IRemoveProjectRoleBody,
  ProjectKeyReference,
  ProjectTemplateImportOptions,
  ProjectTypeTaskBundles
} from '@awork/features/project/services/project-service/types'
import { ProjectBatchOperation, projectBatchURLMap } from '@awork/features/project/services/project-service/types'
import { ProjectKeyValidationError } from '@awork/features/project/services/project-service/types'
import { ProjectTemplateStore } from '@awork/features/project/state/project-template.store'
import { compressFilterBy } from '@awork/_shared/functions/compress-string'
import type { SyncResponse } from '@awork/core/state/signal-store/types'
import type { LinkWorkflowForm } from '@awork/features/workflow/models/link-workflow-form.model'
import type {
  IWorkflowCustomFieldDefinitionLinkEntity,
  IWorkflowScopedEntity,
  WebSocketSignal
} from '@awork/_shared/services/signal-service/signal.model'
import { CustomFieldDefinitionLink } from '@awork/features/project/models/custom-field-definition-link.model'
import type { ICustomFieldDefinition } from '@awork/features/project/models/custom-field-definition.model'
import { TaskService } from '@awork/features/task/services/task-service/task.service'
import { CustomFieldService } from '@awork/features/project/services/custom-field/custom-field.service'
import { TaskStatus } from '@awork/features/task/models/task-status.model'
import { TaskStatusStore } from '@awork/features/task/state/task-status.store'
import { ProjectStatusStore } from '@awork/features/project/state/project-status.store'
import { ProjectStatusQuery } from '@awork/features/project/state/project-status.query'

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private apiClient = inject(ApiClient)
  private userQuery = inject(UserQuery)
  private projectStore = inject(ProjectStore)
  private projectQuery = inject(ProjectQuery)
  private signalService = inject(SignalService)
  private projectTypeStore = inject(ProjectTypeStore)
  private timeTrackingStore = inject(TimeTrackingStore)
  private permissionsService = inject(PermissionsService)
  private router = inject(Router)
  private fileService = inject(FileService)
  private taskStore = inject(TaskStore)
  private timeBookingStore = inject(TimeBookingStore)
  private projectTemplateStore = inject(ProjectTemplateStore)
  private taskService = inject(TaskService)
  private customFieldService = inject(CustomFieldService)
  private taskStatusStore = inject(TaskStatusStore)
  private projectStatusStore = inject(ProjectStatusStore)
  private projectStatusQuery = inject(ProjectStatusQuery)
  private apiCacheService = inject(ApiCacheService)
  private injector = inject(Injector)

  private readonly url: string
  private readonly typeUrl: string
  private readonly templateUrl: string
  private readonly publicTemplateUrl: string
  private readonly roleUrl: string
  private readonly statusUrl: string
  private readonly milestonesUrl: string
  private readonly autopilotUrl: string
  private readonly projectConnectionsUrl: string
  private readonly trustedProjectConnectionsUrl: string
  private readonly syncProjectsRequest = new SharedRequest<SyncResponse<Project>>()
  totalPages = 0
  totalItems = 0

  // Used to know when a project has been created/updated/deleted
  projectCreated = new Subject<Project>()
  projectUpdated = new Subject<Project>()
  projectDeleted = new Subject<string>()
  projectChanged = merge(this.projectCreated, this.projectUpdated, this.projectDeleted)
  projectClosed = new Subject<Project>()
  // Members that a new project could not add. Mobile has no global error toast, so it shows these itself.
  projectMemberAddFailed = new Subject<ProjectMember>()

  fetchCanceled = new Subject<boolean>()

  private fetchProjectMembersCall$: Observable<ProjectMember[]>
  private lastProjectMembersIdFetched: string
  private expireProjectMembersCall = new Subject<void>()

  constructor() {
    this.url = `${apiEndpoint}/projects`
    this.typeUrl = `${apiEndpoint}/projecttypes`
    this.templateUrl = `${apiEndpoint}/projecttemplates`
    this.publicTemplateUrl = `${apiEndpoint}/publicprojecttemplates`
    this.roleUrl = `${apiEndpoint}/projectroles`
    this.statusUrl = `${apiEndpoint}/projectstatuses`
    this.milestonesUrl = `${apiEndpoint}/projectmilestones`
    this.autopilotUrl = `${apiEndpoint}/autopilot`
    this.projectConnectionsUrl = `${apiEndpoint}/projectconnections`
    this.trustedProjectConnectionsUrl = `${apiEndpoint}/trustedprojectconnections`

    this.registerSignal()

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.fetchProjectMembersCall$ = undefined
      this.expireProjectMembersCall.next()
    })
  }

  /**
   * Gets the project signals and triggers the sync on reconnection
   */
  private registerSignal(): void {
    const projectSignals$ = this.signalService.getSignal('project')

    projectSignals$.subscribe(signal => this.onProjectWebsocketUpdated(signal))

    // Keep the project store synchronization behavior unchanged.
    this.signalService.getSignal('project', this.projectStore, this.projectDeleted)

    // Invalidate before store sync so deleted signals can still resolve the previous status scope from the store.
    this.signalService.getSignal('projectstatus').subscribe(signal => this.handleProjectStatusSignal(signal))

    // Keep the project status store in sync with websocket updates.
    this.signalService.getSignal('projectstatus', this.projectStatusStore)

    this.signalService
      .getSignal('taskstatus')
      .pipe(filter(signal => this.isWorkflowScopedSignal(signal)))
      .subscribe(signal => {
        const linkedProjectIds = this.getLinkedProjectIds(signal)
        linkedProjectIds.forEach(projectId => {
          if (signal.operationType === 'deleted') {
            this.taskStatusStore.remove(signal.entityId)
          } else {
            const status = new TaskStatus({ ...(signal.entity as TaskStatus), projectId, workflowId: undefined })

            this.taskStatusStore.syncEntity(status)
          }
        })
      })

    this.signalService
      .getSignal('workflowtocustomfielddefinitions')
      .pipe(filter(signal => this.isWorkflowScopedSignal(signal)))
      .subscribe(signal => {
        const entity = signal.entity as IWorkflowCustomFieldDefinitionLinkEntity
        const linkedProjectIds = this.getLinkedProjectIds(signal)

        linkedProjectIds.forEach(projectId => {
          if (signal.operationType === 'deleted') {
            this.projectStore.update(projectId, project => ({
              customFieldDefinitionLinks: arrayRemove(
                project.customFieldDefinitionLinks,
                entity.customFieldDefinitionId
              )
            }))
          } else if (entity.customFieldDefinition) {
            const link = new CustomFieldDefinitionLink({
              ...(entity.customFieldDefinition as unknown as ICustomFieldDefinition),
              order: entity.order ?? 0,
              projectId
            })

            this.projectStore.update(projectId, project => ({
              customFieldDefinitionLinks: project.customFieldDefinitionLinks?.some(l => l.id === link.id)
                ? arrayUpdate(project.customFieldDefinitionLinks, link.id, link)
                : arrayAdd(project.customFieldDefinitionLinks, link)
            }))
          }
        })
      })

    this.signalService.reconnected.subscribe(() => {
      if (!this.projectQuery.queryLoading()) {
        this.syncProjects().subscribe()
      }
    })
  }

  /**
   * Invalidates cached project status requests affected by a websocket status signal.
   * @param {WebSocketSignal} signal The project status websocket signal
   */
  private handleProjectStatusSignal(signal: WebSocketSignal): void {
    const projectStatus = signal.entity
      ? new ProjectStatus(signal.entity as ProjectStatus)
      : this.projectStatusQuery.getEntity(signal.entityId)

    this.apiCacheService.invalidate(
      projectStatus ? this.getProjectStatusCacheScope(projectStatus) : { entity: ProjectStatus }
    )

    this.invalidateLinkedProjectStatusCache(signal)
  }

  /**
   * Invalidates project-scoped status caches for projects linked to a workflow status signal.
   * @param {WebSocketSignal} signal The workflow-scoped project status signal
   */
  private invalidateLinkedProjectStatusCache(signal: WebSocketSignal): void {
    if (!this.isWorkflowScopedSignal(signal)) {
      return
    }

    this.getLinkedProjectIds(signal).forEach(projectId => {
      this.apiCacheService.invalidate({ entity: ProjectStatus, property: 'projectId', value: projectId })
    })
  }

  /**
   * Handles project websocket updates and dispatches feature-specific reactions.
   * @param {WebSocketSignal} signal
   */
  private onProjectWebsocketUpdated(signal: WebSocketSignal): void {
    this.handleProjectWorkflowIdTransition(signal)
    this.handlePrivateProjectAccessAfterSignal(signal)
  }

  /**
   * Handles project websocket signals and detects workflowId transitions.
   * On transition, refreshes project-specific statuses and custom field links.
   * @param {WebSocketSignal} signal
   */
  private handleProjectWorkflowIdTransition(signal: WebSocketSignal): void {
    if (signal.operationType !== 'added' && signal.operationType !== 'updated') {
      return
    }

    const incomingProject = signal.entity as Project

    if (!incomingProject?.id) {
      return
    }

    const previousProject = this.projectQuery.getProject(incomingProject.id)
    const previousWorkflowId = previousProject?.workflowId || null
    const nextWorkflowId = incomingProject.workflowId || null

    if (previousWorkflowId === nextWorkflowId) {
      return
    }

    this.refreshProjectWorkflowDependentData(incomingProject.id)
  }

  /**
   * Removes private projects from local stores when the current user no longer has access.
   * This runs in a microtask so the project signal has already been applied to stores.
   * @param {WebSocketSignal} signal
   */
  private handlePrivateProjectAccessAfterSignal(signal: WebSocketSignal): void {
    if (signal.operationType !== 'added' && signal.operationType !== 'updated') {
      return
    }

    const incomingProject = signal.entity as Project

    if (!incomingProject?.id) {
      return
    }

    queueMicrotask(() => {
      const projectInStore = this.projectQuery.getProject(incomingProject.id)

      if (!projectInStore) {
        return
      }

      const currentUser = this.userQuery.getCurrentUser()

      if (!currentUser) {
        return
      }

      if (this.shouldRemoveProjectForUser(projectInStore, currentUser.id)) {
        this.removeProjectDataFromStores(projectInStore.id)
      }
    })
  }

  /**
   * Refreshes project-dependent data affected by workflow linking changes.
   * This explicitly supports transition cases: null -> workflowId, workflowA -> workflowB, workflowId -> null.
   * @param {string} projectId
   */
  private refreshProjectWorkflowDependentData(projectId: string): void {
    this.apiCacheService.invalidate({ entity: ProjectStatus, property: 'projectId', value: projectId })
    this.taskService.fetchTaskStatuses({}, projectId).subscribe()
    this.customFieldService.fetchProjectCustomFieldDefinitionLinks(projectId, 'projects').subscribe()
  }

  /**
   * Determines whether a websocket signal is scoped to a workflow entity payload.
   * @param {WebSocketSignal} signal
   * @returns {boolean}
   */
  private isWorkflowScopedSignal(signal: WebSocketSignal): boolean {
    const entity = signal.entity as IWorkflowScopedEntity

    return !!entity?.workflowId
  }

  /**
   * Extracts unique linked project ids from a workflow-scoped websocket signal.
   * @param {WebSocketSignal} signal
   * @returns {string[]}
   */
  private getLinkedProjectIds(signal: WebSocketSignal): string[] {
    const entity = signal.entity as IWorkflowScopedEntity

    if (!entity?.linkedProjectIds?.length) {
      return []
    }

    return Array.from(new Set(entity.linkedProjectIds.filter(projectId => !!projectId)))
  }

  // ########### PROJECTS ##############

  /**
   * Makes an API call to fetch the project
   * @param {string} id The id of the project
   * @param {boolean} [permissionRedirect=true] To redirect to the permission denied page
   * @param {boolean} [setActive=false] To set the project as the active project in the store
   * @returns {Observable<Project>} The project object
   */
  fetchProject(id: string, permissionRedirect = true, setActive = false): Observable<Project> {
    return this.apiClient.get<Project>(`${this.url}/${id}`).pipe(
      map(project => {
        project = Project.mapProject(project)

        this.projectStore.upsert(project.id, project)

        if (setActive) {
          this.projectStore.setActive(project.id)
        }

        return project
      }),
      catchError(error => {
        if (error.status === 401 || error.status === 404) {
          const project = this.projectQuery.getEntity(id)

          if (
            project?.isExternal ||
            !this.permissionsService.isAllowed(Features.ProjectMaster, AccessLevels.Read, permissionRedirect)
          ) {
            try {
              this.projectStore.remove(id)
            } catch (_) {}
          }
        }

        return throwError(error)
      })
    )
  }

  /**
   * Makes an API call to request the project information
   * @param {string} id The id of the project
   * @param {boolean} [permissionRedirect=true]
   * @returns {Observable<Project>} The project information, is null if there is some error (ex.: Project not found)
   * @deprecated
   */
  getProject(id: string, permissionRedirect = true): Observable<Project> {
    let fetched = false
    return this.projectQuery.selectProject(id, this.injector).pipe(
      mergeMap(project => {
        // If the project has been found, return it and make an API call to fetch it fresh (just once)
        if (project) {
          if (!fetched) {
            fetched = true
            this.fetchProject(id, permissionRedirect).pipe(takeUntil(this.fetchCanceled)).subscribe()
          }
          // else, fetch it from the API (just once)
        } else if (!fetched) {
          fetched = true

          return this.fetchProject(id, permissionRedirect)
        }

        return of(project)
      })
    )
  }

  /**
   * Resolves a project Id by its projectKey
   * @param {string} projectKey
   * @param {boolean} [getExternal=false]
   * @returns {Observable<string | null>}
   */
  getProjectIdByKey(projectKey: string, getExternal = false): Observable<string | null> {
    return this.getProjectReferencesByKey(projectKey).pipe(
      map(projects => projects.find(project => project.isExternal === getExternal)?.id ?? null)
    )
  }

  /**
   * Resolves every accessible project reference for a project key.
   * Duplicate keys can exist across connected projects, so callers that navigate by key
   * must inspect the complete result rather than selecting the first match.
   * @param {string} projectKey
   * @returns {Observable<ProjectKeyReference[]>}
   */
  getProjectReferencesByKey(projectKey: string): Observable<ProjectKeyReference[]> {
    const queryParams: QueryParams = {
      filterBy: `projectKey eq '${projectKey.toUpperCase()}'`,
      select: 'Id,isExternal,createdOn'
    }

    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .get<Project[]>(this.url, { params })
      .pipe(map(projects => projects?.map(project => ({ id: project.id, isExternal: project.isExternal })) ?? []))
  }

  /**
   * Makes an API call to request all the projects
   * @param {QueryParams} [queryParams={}]
   * @param {Signal<Project[]>} projectsToSync
   * @param {boolean} [getAll=true] To get all projects or just the specified page
   * @returns {Observable<Project[]>} The project information, null if error (ex.: Project not found)
   */
  fetchProjects(
    queryParams: QueryParams = {},
    projectsToSync?: Signal<Project[]>,
    getAll = true
  ): Observable<Project[]> {
    let syncStore = false

    // eslint-disable-next-line @angular-eslint/no-uncalled-signals
    if (!queryParams.filterBy || !!projectsToSync) {
      syncStore = true
    }

    if (!queryParams.orderBy) {
      queryParams.orderBy = 'projectStatus/typeOrder asc,createdOn desc'
    }

    if (!queryParams.pageSize) {
      queryParams.pageSize = 1500
    }

    if (!queryParams.page) {
      queryParams.page = 1
    }

    return compressFilterBy(queryParams).pipe(
      switchMap(compressedQueryParams => {
        const params: HttpParams = ApiClient.getQueryParams(compressedQueryParams)

        const callOptions: Options = {
          params,
          observe: 'response'
        }

        const call$ = getAll
          ? this.apiClient.getAll<HttpResponse<Project[]>>(this.url, callOptions)
          : this.apiClient.get<HttpResponse<Project[]>>(this.url, callOptions)

        return call$.pipe(
          map((response: HttpResponse<Project[]>) => {
            const projects: Project[] = response.body.map(project => Project.mapProject(project))

            // setting the total page count
            this.totalItems = Number(response.headers.get('aw-totalitems'))

            const pageSize: number = queryParams.pageSize

            this.totalPages = Math.ceil(this.totalItems / pageSize)

            if (syncStore) {
              const allProjects = this.projectQuery.queryAllProjects()

              this.projectStore.sync(projects, projectsToSync?.() || allProjects(), {
                paging: { page: queryParams.page, pageSize: queryParams.pageSize }
              })
            } else {
              this.projectStore.add(projects)
              this.projectStore.setLoading(false)
            }

            return projects
          }),
          catchError(error => {
            if (error.status === 401) {
              this.projectStore.setLoading(false)
            }

            return throwError(error)
          })
        )
      })
    )
  }

  // TODO: This logic should be integrated in the fetchProjects method.
  // I decided not to do this now because we should see first if by adding this sync logic it could affect other components
  // as that method is used in many places.
  /**
   * Use this method when its necessary to sync the store with projects fetched using a filterBy expression
   * @param {string} filterBy
   * @returns {Observable<Project[]>}
   */
  fetchAndSyncProjects(filterBy: string): Observable<Project[]> {
    return this.fetchProjects({ filterBy }).pipe(
      map(projects => {
        const projectEntities = projects.map(project => new Project(project))
        const projectEntitiesIds = projectEntities.map(task => task.id)
        const projectsToSync = this.projectQuery.queryProjects(projectEntitiesIds)

        this.projectStore.sync(projectEntities, projectsToSync())

        return projectEntities
      })
    )
  }

  /**
   * Makes an API call to get the sync information of projects and updates the store
   * @returns {Observable<SyncResponse<Project>>}
   */
  syncProjects(): Observable<SyncResponse<Project>> {
    return this.syncProjectsRequest.share(() => {
      const lastSync = this.projectQuery.queryLastSync()
      const requiresFullSync = this.projectQuery.requiresFullSync(project => !project.isExternal)
      let request$: Observable<SyncResponse<Project>>

      if (requiresFullSync) {
        request$ = this.fetchProjects().pipe(
          map(projects => {
            const lastUpdatedOn = this.projectQuery.queryLastUpdatedOn()

            return {
              updated: projects,
              deleted: [],
              lastSync: getLatestSyncDate(lastSync, lastUpdatedOn())
            }
          }),
          tap(syncResponse => {
            this.projectStore.setLastSync(syncResponse.lastSync)
          })
        )
      } else {
        const issuedAtGeneration = this.projectStore.getResetGeneration()
        const queryParams = {
          since: lastSync.toISOString()
        }

        request$ = this.apiClient
          .get<SyncResponse<Project>>(`${apiEndpoint}/sync/project`, { params: queryParams })
          .pipe(
            tap(response => {
              response = this.normalizeProjectSyncResponse(response)
              this.removeProjectScopedDataFromSync(response)
              this.projectStore.apiSync(response, { issuedAtGeneration })
            })
          )
      }

      return request$
    })
  }

  /**
   * Makes an API call to get the sync information of filtered projects and updates the store
   * @param {string} filterBy
   * @returns {Observable<SyncResponse<Project>>}
   */
  syncFilteredProjects(filterBy: string): Observable<SyncResponse<Project>> {
    // A delta sync only returns changes since the cursor. Without a cursor, or when the store lost
    // projects the filter still tracks, only a full fetch can restore the missing projects.
    if (this.projectQuery.requiresFilteredFullSync(filterBy)) {
      return this.fetchProjects({ filterBy }).pipe(
        map(projects => {
          const fetchedIds = projects.map(project => project.id)
          const lastUpdatedOn = this.projectQuery.queryLastUpdatedOn(fetchedIds)

          return {
            updated: projects,
            deleted: [],
            lastSync: lastUpdatedOn()
          }
        }),
        tap(response => {
          this.projectStore.setSyncFilter(filterBy, response)
        })
      )
    }

    const syncFilter = this.projectQuery.querySyncFilter(filterBy)
    const queryParams = {
      since: syncFilter().lastSync.toISOString(),
      filterBy
    }

    return this.apiClient.get<SyncResponse<Project>>(`${apiEndpoint}/sync/project`, { params: queryParams }).pipe(
      tap(response => {
        response = this.normalizeProjectSyncResponse(response)
        this.removeProjectScopedDataFromSync(response)
        this.projectStore.apiSyncFiltered(filterBy, response)
      })
    )
  }

  /**
   * Normalizes project sync responses by explicitly setting nullable workflowId from payload.
   * Ensures undefined workflowId from sync endpoint is treated as null and clears store state.
   * @param {SyncResponse<Project>} response
   * @returns {SyncResponse<Project>}
   */
  private normalizeProjectSyncResponse(response: SyncResponse<Project>): SyncResponse<Project> {
    if (!response?.updated?.length) {
      return response
    }

    const updated = response.updated.map(project => {
      project.workflowId = project.workflowId ?? null
      return project
    })

    return { ...response, updated }
  }

  /**
   * Removes project-scoped entities when project sync reports removed projects.
   * @param {SyncResponse<Project>} response
   */
  private removeProjectScopedDataFromSync(response: SyncResponse<Project>): void {
    const removedProjectIds = new Set([...(response.deleted ?? []), ...(response.inaccessible ?? [])])

    if (!removedProjectIds.size) {
      return
    }

    this.taskStore.remove(task => removedProjectIds.has(task.projectId))
    this.timeBookingStore.remove(timeBooking => removedProjectIds.has(timeBooking.projectId))
  }

  /**
   * Fetches projects by ids
   * @param {string[]} projectIds
   * @returns {Observable<Project[]>}
   */
  fetchProjectsById(projectIds: string[]): Observable<Project[]> {
    const queryParamsFn = (projectIds: string[]) => ({
      filterBy: projectIds.map(id => `id eq guid'${id}'`).join(' or ')
    })
    const callFn = (queryParams: QueryParams) => this.fetchProjects(queryParams)

    return this.apiClient.getBatch(projectIds, queryParamsFn, callFn).pipe(
      map(projects => {
        projects = projects.map(project => Project.mapProject(project))

        const projectsToSync = this.projectQuery.queryProjects(projectIds)

        this.projectStore.sync(projects, projectsToSync())

        return projects
      })
    )
  }

  /**
   * Sends sequential project batches and reconciles each confirmed result in the store.
   * Partial request failures include completed IDs; accepted requests continue after view dismissal.
   * @param {string[]} projectIds
   * @param {ProjectBatchOperation} operationName
   * @param {ProjectBatchRequestBody} requestData
   * @returns {Observable<MultiStatusResponse | null>}
   */
  batchEdit(
    projectIds: string[],
    operationName: ProjectBatchOperation,
    requestData: ProjectBatchRequestBody
  ): Observable<MultiStatusResponse | null> {
    const batchURL = `${this.url}/batch/${projectBatchURLMap[operationName]}`
    const completedProjectIds = new Set<string>()

    return from(projectIds).pipe(
      bufferCount(MAX_BATCH_OPERATION_ITEMS),
      concatMap(batchProjectIds =>
        this.apiClient
          .post<HttpResponse<void | MultiStatusResponse>>(
            batchURL,
            { ...requestData, projectIds: batchProjectIds },
            { observe: 'response' }
          )
          .pipe(
            switchMap(response => {
              const multiStatus = response.body as MultiStatusResponse
              const updatedProjectIds = response.status === 207 ? (multiStatus?.ok?.ids ?? []) : batchProjectIds
              const result: MultiStatusResponse = {
                ok: { ids: updatedProjectIds },
                notFound: { ids: multiStatus?.notFound?.ids ?? [] },
                unauthorized: { ids: multiStatus?.unauthorized?.ids ?? [] },
                badRequest: { ids: multiStatus?.badRequest?.ids ?? [] }
              }

              updatedProjectIds.forEach(id => completedProjectIds.add(id))

              if (!updatedProjectIds.length) {
                return of(result)
              }

              if (operationName === ProjectBatchOperation.delete) {
                const deleteBody: IProjectDeleteBody = {
                  deleteTimeTrackings: 'deleteTimeTrackings' in requestData && requestData.deleteTimeTrackings === true
                }

                this.projectStore.remove(updatedProjectIds)
                updatedProjectIds.forEach(projectId => {
                  this.updateProjectTimeTrackings(projectId, deleteBody)
                  this.projectDeleted.next(projectId)
                })

                return of(result)
              }

              return this.fetchProjectsById(updatedProjectIds).pipe(
                map(projects => {
                  projects.forEach(project => this.projectUpdated.next(project))
                  return result
                })
              )
            })
          )
      ),
      toArray(),
      map(results => ({
        ok: { ids: results.flatMap(result => result.ok.ids) },
        notFound: { ids: results.flatMap(result => result.notFound.ids) },
        unauthorized: { ids: results.flatMap(result => result.unauthorized.ids) },
        badRequest: { ids: results.flatMap(result => result.badRequest.ids) }
      })),
      catchError(error => {
        if (completedProjectIds.size) {
          return throwError(() => new ProjectBatchOperationError(error, [...completedProjectIds]))
        }

        return throwError(() => error)
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }

  /**
   * Makes an API call to fetch all assigned active projects
   * @param {QueryParams} [queryParams={}]
   * @returns {Observable<Project[]>}
   * @deprecated - use queryAssignedActiveProjects from ProjectQuery as projects are synced
   */
  fetchAssignedActiveProjects(queryParams: QueryParams = {}): Observable<Project[]> {
    let syncStore = false

    if (!queryParams.filterBy) {
      syncStore = true
    }

    if (!queryParams.pageSize) {
      queryParams.pageSize = 1500
    }

    if (!queryParams.page) {
      queryParams.page = 1
    }

    if (!queryParams.orderBy) {
      queryParams.orderBy = 'Name asc'
    }

    const assignedFilter = `projectStatus/type eq 'progress' and members/any(m: m/userId eq guid'${
      this.userQuery.getCurrentUser().id
    }')`

    queryParams.filterBy = queryParams.filterBy ? `${queryParams.filterBy} and ${assignedFilter}` : assignedFilter

    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .get<HttpResponse<Project[]>>(this.url, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map((response: HttpResponse<Project[]>) => {
          const projects: Project[] = response.body.map(project => Project.mapProject(project))

          // setting the total page count
          this.totalItems = Number(response.headers.get('aw-totalitems'))

          const pageSize: number = queryParams.pageSize

          this.totalPages = Math.ceil(this.totalItems / pageSize)

          if (syncStore) {
            const projectsToSync = this.projectQuery.queryAssignedActiveProjects()

            this.projectStore.sync(projects, projectsToSync(), {
              paging: { page: queryParams.page, pageSize: queryParams.pageSize }
            })
          }

          return projects
        })
      )
  }

  /**
   * Fetches the users' projects within the given date range
   * @param {{ userIds: string[]; startDate: Date; endDate: Date; }} filter
   * @param filter.userIds
   * @param {boolean} [excludeClosedProjects=true]
   * @param filter.startDate
   * @param filter.endDate
   * @returns {Observable<Project[]>}
   */
  fetchProjectsInDateRange(
    filter: { userIds: string[]; startDate: Date; endDate: Date },
    excludeClosedProjects = true
  ): Observable<Project[]> {
    const userIdsFilter = filter.userIds
      ? '(' + filter.userIds.map(userId => `members/any(m: m/userId eq guid'${userId}')`).join(' or ') + ')'
      : null

    const startOnFilter = filter.startDate ? `startDate le datetime'${formatDateForFilter(filter.endDate)}'` : null
    const endOnFilter = filter.endDate ? `dueDate ge datetime'${formatDateForFilter(filter.startDate)}'` : null

    const queryParams: QueryParams = {
      filterBy: [userIdsFilter, startOnFilter, endOnFilter].filter(filter => !!filter).join(' and ')
    }

    if (excludeClosedProjects) {
      queryParams.filterBy = `${queryParams.filterBy} and projectStatus/type ne 'closed'`
    }

    return this.fetchProjects(queryParams).pipe(
      switchMap(projects => {
        return of(projects)
      }),
      tap(projects => {
        if (projects.length) {
          const projectsToSync = this.projectQuery.queryProjects(projects.map(project => project.id))

          this.projectStore.sync(projects, projectsToSync())
        }
      })
    )
  }

  /**
   * Makes an API call to create a project
   * @param {Project} project The project to be created
   * @param {ProjectTemplate | PublicProjectTemplate} projectTemplate
   * @returns {Observable<Project>} The project information
   */
  sendProject(project: Project, projectTemplate?: ProjectTemplate | PublicProjectTemplate): Observable<Project> {
    const projectPost = {
      plannedDuration: project.plannedDuration,
      projectTypeId: project.projectTypeId,
      projectStatusId: project.projectStatusId,
      companyId: project.companyId,
      name: project.name,
      description: trimRichText(project.description),
      startDate: dateToISO(project.startDate),
      dueDate: dateToISO(project.dueDate),
      timeBudget: project.isRetainer ? undefined : project.timeBudget,
      isBillableByDefault: project.isBillableByDefault,
      projectTemplateId: project.projectTemplateId,
      publicProjectTemplateId: project.publicProjectTemplateId,
      isPrivate: project.isPrivate,
      deductNonBillableHours: project.deductNonBillableHours,
      workflowId: project.workflowId,
      referenceDateType: projectTemplate?.referenceDateType,
      referenceDateAnchor: projectTemplate?.referenceDateAnchor,
      customReferenceDate: projectTemplate?.customReferenceDate
        ? extractDateOnlyISOString(projectTemplate.customReferenceDate)
        : undefined,
      members: project.members?.map(member => ({
        userId: member.userId,
        projectRoleId: member.projectRoleId,
        isResponsible: member.isResponsible
      }))
    }

    return this.apiClient.post<Project>(`${this.url}`, projectPost).pipe(
      switchMap(newProject => {
        newProject = Project.mapProject(newProject)

        const savedMembers = newProject.members ?? []
        const unsavedMembers: ProjectMember[] = []

        project.members?.forEach(member => {
          const savedMember = savedMembers.find(saved => saved.userId === member.userId)

          if (savedMember) {
            member.id = savedMember.id
          } else {
            unsavedMembers.push(member)
          }
        })

        Object.assign(project, { ...newProject, members: project.members, tags: project.tags, teams: project.teams })
        project = Project.mapProject(project)
        this.projectCreated.next(project)
        this.projectStore.add(project)

        return this.addUnsavedProjectMembers(project, unsavedMembers)
      })
    )
  }

  /**
   * Adds the members that the create request did not save, one request each and the responsible member last.
   * This covers an API that does not accept members on create, and members that the API rejected,
   * so their errors still show. A failed member request does not fail the project creation;
   * it is emitted on projectMemberAddFailed instead.
   * @param {Project} project The created project
   * @param {ProjectMember[]} unsavedMembers The members missing in the create response
   * @returns {Observable<Project>} The created project once all member requests finished
   */
  private addUnsavedProjectMembers(project: Project, unsavedMembers: ProjectMember[]): Observable<Project> {
    if (!unsavedMembers.length) {
      return of(project)
    }

    const orderedMembers = [...unsavedMembers].sort(
      (memberA, memberB) => Number(memberA.isResponsible) - Number(memberB.isResponsible)
    )

    return concat(
      ...orderedMembers.map(member =>
        this.addProjectMember(project, member).pipe(
          catchError(() => {
            this.projectMemberAddFailed.next(member)

            return of(null)
          })
        )
      )
    ).pipe(
      toArray(),
      map(() => project)
    )
  }

  /**
   * Makes an API call to update the project
   * @param {Project} project Project to be updated
   * @param {Record<string, string>} [queryParams={}]
   * @param {keyof Project} propertyName
   * @returns {Observable<Project>}
   */
  updateProject(
    project: Project,
    queryParams: Record<string, string> = {},
    propertyName?: keyof Project
  ): Observable<Project> {
    const projectPost = {
      plannedDuration: project.plannedDuration,
      projectTypeId: project.projectTypeId,
      projectStatusId: project.projectStatusId,
      companyId: project.companyId,
      name: project.name,
      description: trimRichText(project.description),
      startDate: dateToISO(project.startDate),
      dueDate: dateToISO(project.dueDate),
      timeBudget: project.isRetainer ? undefined : project.timeBudget,
      isBillableByDefault: project.isBillableByDefault,
      isPrivate: project.isPrivate,
      color: project.color,
      deductNonBillableHours: project.deductNonBillableHours,
      isProjectKeyVisible: project.isProjectKeyVisible
    }

    const previousProject = this.projectQuery.getEntity(project.id) ?? project
    const operationId = this.projectStore.history.startOperation({ type: 'update', entities: [previousProject] })

    // Update project store optimistically
    this.projectStore.update(project.id, project)

    const optimisticProject = this.projectQuery.getEntity(project.id)

    return this.apiClient
      .put<Project>(`${this.url}/${project.id}/`, projectPost, {
        params: queryParams,
        entityName: 'project',
        propertyName
      })
      .pipe(
        map(updatedProject => {
          updatedProject = Project.mapProject(updatedProject)
          this.taskStore.updateProjectsTasks(updatedProject)
          this.timeBookingStore.updateProjectsTimeBookings(updatedProject)
          Object.assign(project, { ...updatedProject, tags: project.tags, members: project.members })
          this.projectUpdated.next(project)
          this.projectStore.history.endOperation(operationId)

          return project
        }),
        catchError(error => {
          if (this.projectQuery.getEntity(project.id) === optimisticProject) {
            this.projectStore.history.undo(operationId)
          } else {
            this.projectStore.history.endOperation(operationId)
          }

          return throwError(() => error)
        })
      )
  }

  /**
   * Updates the project in the store
   * @param {Project} project
   */
  updateProjectInStore(project: Project): void {
    this.projectStore.update(project.id, project)
  }

  /**
   * Makes an API call to update the project key
   * @param {string} projectId The id of the project
   * @param {string} projectKey The new project key
   * @returns {Observable<void>}
   */
  updateProjectKey(projectId: string, projectKey: string): Observable<void> {
    return this.apiClient
      .post<void>(`${this.url}/${projectId}/setProjectKey`, {
        projectKey
      })
      .pipe(
        map(() => {
          this.projectStore.update(projectId, { projectKey })
        })
      )
  }

  /**
   * Validates if a project key is valid and available
   * @param {string} projectKey
   * @returns {Observable<void>}
   */
  validateProjectKey(projectKey: string): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/validateProjectKey`, { projectKey }).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorCode = error.error?.code as ProjectKeyValidationError

        if (Object.values(ProjectKeyValidationError).includes(errorCode)) {
          return throwError(() => errorCode)
        }

        return throwError(() => error)
      })
    )
  }

  /**
   * Makes an API call to delete the project
   * @param {Project} project The project to be deleted
   * @param {IProjectDeleteBody} body
   * @returns {Observable<Project>}
   */
  deleteProject(project: Project, body: IProjectDeleteBody): Observable<Project> {
    return this.apiClient.post<Project>(`${this.url}/${project.id}/delete`, body).pipe(
      map(() => {
        this.projectDeleted.next(project.id)
        this.projectStore.remove(project.id)
        this.updateProjectTimeTrackings(project.id, body)

        return project
      })
    )
  }

  /**
   * Starts copying a project and returns the created shell project id.
   * @param {string} projectId The source project id
   * @param {CopyProjectForm} form Copy options
   * @returns {Observable<CopyProjectResponse>}
   */
  copyProject(projectId: string, form: CopyProjectForm): Observable<CopyProjectResponse> {
    return this.apiClient.post<CopyProjectResponse>(`${this.url}/${projectId}/copy`, form)
  }

  /**
   * Reconciles cached time entries after confirmed project deletion.
   * @param {string} projectId Deleted project ID.
   * @param {IProjectDeleteBody} body Time-entry deletion options.
   */
  private updateProjectTimeTrackings(projectId: string, body: IProjectDeleteBody): void {
    const timeTrackingOperation = body.deleteTimeTrackings ? 'delete' : 'unlink'
    this.timeTrackingStore.updateEntityTimeTrackings('project', projectId, timeTrackingOperation)
  }

  // ########### PROJECT MEMBERS ##############

  /**
   * Makes an API call to request the project members
   * This call use caching that expires when a navigation happens
   * @param {string} projectId The id of the project
   * @returns {Observable<ProjectMember[]>} The project members information,
   */
  fetchProjectMembers(projectId: string): Observable<ProjectMember[]> {
    if (this.fetchProjectMembersCall$ && this.lastProjectMembersIdFetched === projectId) {
      return this.fetchProjectMembersCall$
    } else {
      this.expireProjectMembersCall.next()
    }

    this.lastProjectMembersIdFetched = projectId

    return (this.fetchProjectMembersCall$ = this.apiClient
      .get<ProjectMember[]>(`${this.url}/${projectId}/members`)
      .pipe(
        map(members => {
          members = members.map(member => new ProjectMember(member))

          this.projectStore.update(projectId, { members })

          return members
        }),
        shareReplay(),
        takeUntil(this.expireProjectMembersCall)
      ))
  }

  /**
   * Makes an API call to add a project member to a project
   * @param {Project} project The project whose members are to be updated
   * @param {ProjectMember} member The project member to be added to the project
   * @returns {Observable<ProjectMember>}
   */
  addProjectMember(project: Project, member: ProjectMember): Observable<ProjectMember> {
    return this.apiClient
      .post<ProjectMember>(`${this.url}/${project.id}/addprojectmember`, {
        userId: member.userId,
        projectRoleId: member.projectRoleId,
        isResponsible: member.isResponsible
      })
      .pipe(
        map(savedMember => {
          savedMember = new ProjectMember(savedMember)

          const projectMember = project.members.find(m => m.userId === savedMember.userId)

          if (!projectMember) {
            project.members.push(savedMember)
          } else if (!projectMember.id) {
            projectMember.id = savedMember.id
          }

          this.projectStore.update(project.id, project)
          this.projectUpdated.next(project)

          return savedMember
        })
      )
  }

  /**
   * Makes an API call to update a project member in a project
   * @param {Project} project The project whose members are to be updated
   * @param {ProjectMember} member The project member to be updated in the project
   * @returns {Observable<ProjectMember>}
   */
  updateProjectMember(project: Project, member: ProjectMember): Observable<ProjectMember> {
    return this.apiClient
      .post<ProjectMember>(`${this.url}/${project.id}/updateprojectmember`, {
        userId: member.userId,
        projectRoleId: member.projectRoleId,
        isResponsible: member.isResponsible
      })
      .pipe(
        map(savedMember => {
          savedMember = new ProjectMember(savedMember)

          project.members = project.members.map(m => (m.userId === member.userId ? savedMember : m))

          this.projectStore.update(project.id, project)
          this.projectUpdated.next(project)

          return savedMember
        })
      )
  }

  /**
   * Updates a group of project members
   * @param {Project} updatedProject
   * @param {ProjectMember[]} members
   * @returns {Observable<void>}
   */
  updateProjectMembers(updatedProject: Project, members: ProjectMember[]): Observable<void> {
    // optimistically update project with members
    this.projectStore.update(updatedProject.id, updatedProject)

    return forkJoin(members.map(member => this.updateProjectMember(updatedProject, member))).pipe(
      map(() => {
        return
      })
    )
  }

  /**
   * Makes an API call to remove a project member from a project
   * @param {Project} project The project whose members are to be updated
   * @param {ProjectMember} member The project member to be removed from the project
   * @returns {Observable<void>}
   */
  deleteProjectMember(project: Project, member: ProjectMember): Observable<void> {
    if (project?.isPrivate && project.isOnlyMember(member.userId, project?.members)) {
      return throwError(() => new Error('Cannot remove the last member from a private project.'))
    }

    return this.apiClient
      .post<void>(`${this.url}/${project.id}/removeprojectmember`, {
        userId: member.userId
      })
      .pipe(
        map(() => {
          const currentUserId = this.userQuery.getCurrentUser()?.id

          project.members = project.members.filter(m => m.userId !== member.userId)

          if (currentUserId && this.shouldRemoveProjectForUser(project, currentUserId)) {
            this.removeProjectDataFromStores(project.id)

            return
          }

          this.projectStore.update(project.id, project)
          this.projectUpdated.next(project)
        })
      )
  }

  /**
   * Returns whether the given user can no longer access a private project.
   * @param {Project} project
   * @param {string} userId
   * @returns {boolean}
   */
  private shouldRemoveProjectForUser(project: Project, userId: string): boolean {
    if (!project?.isPrivate || !userId) {
      return false
    }

    if (!Array.isArray(project.members)) {
      return false
    }

    return !project.isCreatorOrMember(userId)
  }

  /**
   * Removes project-related entities from local stores.
   * @param {string} projectId
   */
  private removeProjectDataFromStores(projectId: string): void {
    this.projectStore.remove(projectId)
    this.taskStore.remove(task => task.projectId === projectId)
    this.timeBookingStore.remove(timeBooking => timeBooking.projectId === projectId)
    this.timeTrackingStore.remove(timeTracking => timeTracking.projectId === projectId)
    this.projectDeleted.next(projectId)
  }

  // ########### PROJECT TYPES ##############

  /**
   * Makes an API call to request all the project types
   * @param {QueryParams} queryParams
   * @param {boolean} [showArchived=false] Flag to show the archived project types
   * @returns {Observable<ProjectType[]>} The project type information
   */
  fetchProjectTypes(queryParams?: QueryParams, showArchived = false): Observable<ProjectType[]> {
    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    params = params.append('showArchived', showArchived.toString())

    return this.apiClient
      .get<ProjectType[]>(this.typeUrl, {
        params
      })
      .pipe(
        map(projectTypes => {
          if (projectTypes) {
            projectTypes = projectTypes.map(projectType => new ProjectType(projectType))
          }

          this.projectTypeStore.set(projectTypes)

          return projectTypes
        })
      )
  }

  /**
   * Adds profile image
   * @param {Project} project
   * @param {BlobFile} blobFile
   * @returns {Observable<FileUpload>}
   */
  addProfileImage(project: Project, blobFile: BlobFile): Observable<FileUpload> {
    const previousHasImage = this.projectQuery.getEntity(project.id)?.hasImage ?? false

    this.projectStore.update(project.id, { hasImage: true })

    return this.fileService.uploadEntityImage(project, 'projects', blobFile).pipe(
      catchError(error => {
        this.projectStore.update(project.id, { hasImage: previousHasImage })

        return throwError(() => error)
      })
    )
  }

  /**
   * Removes profile image
   * @param {Project} project
   * @returns {Observable<void>}
   */
  removeProfileImage(project: Project): Observable<void> {
    const previousHasImage = this.projectQuery.getEntity(project.id)?.hasImage ?? true

    this.projectStore.update(project.id, { hasImage: false })

    return this.fileService.deleteEntityImage(project, 'projects').pipe(
      catchError(error => {
        this.projectStore.update(project.id, { hasImage: previousHasImage })

        return throwError(() => error)
      })
    )
  }

  /**
   * Makes an API call to request the project type information
   * @param {string} projectTypeId The id of the project type
   * @returns {Observable<ProjectType>} The project type information
   */
  fetchProjectType(projectTypeId: string): Observable<ProjectType> {
    return this.apiClient.get<ProjectType>(`${this.typeUrl}/${projectTypeId}`).pipe(
      map(projectType => {
        projectType = new ProjectType(projectType)

        this.projectTypeStore.upsert(projectType.id, projectType)

        return projectType
      })
    )
  }

  /**
   * Makes an API call to create a project type
   * @param {ProjectType} projectType The project type to be created
   * @returns {Observable<ProjectType>} The project type information
   */
  sendProjectType(projectType: ProjectType): Observable<ProjectType> {
    return this.apiClient
      .post<ProjectType>(`${this.typeUrl}`, {
        name: projectType.name,
        description: projectType.description,
        icon: projectType.icon,
        createDefaultTaskStatuses: true
      })
      .pipe(
        map(newProjectType => {
          newProjectType = new ProjectType(newProjectType)
          Object.assign(projectType, newProjectType)

          this.projectTypeStore.add(projectType)

          return projectType
        })
      )
  }

  /**
   * Makes an API call to update the project type
   * @param {ProjectType} projectType The project type to be updated
   * @returns {Observable<ProjectType>} The project type information
   */
  updateProjectType(projectType: ProjectType): Observable<ProjectType> {
    return this.apiClient
      .put<ProjectType>(`${this.typeUrl}/${projectType.id}/`, {
        name: projectType.name,
        icon: projectType.icon,
        description: projectType.description
      })
      .pipe(
        map(updatedProjectType => {
          updatedProjectType = new ProjectType(updatedProjectType)
          Object.assign(projectType, updatedProjectType)

          this.projectTypeStore.update(projectType.id, projectType)

          return projectType
        })
      )
  }

  /**
   * Makes an API call to delete the project type
   * @param {ProjectType} projectType The project type to be deleted
   * @param {string} newProjectTypeId
   * @returns {Observable<void>}
   */
  deleteProjectType(projectType: ProjectType, newProjectTypeId: string): Observable<void> {
    const body = newProjectTypeId ? { projectTypeId: newProjectTypeId } : ({} as IDeleteProjectTypeBody)

    return this.apiClient.post<void>(`${this.typeUrl}/${projectType.id}/delete`, body).pipe(
      tap(() => {
        this.projectTypeStore.remove(projectType.id)

        if (!newProjectTypeId) {
          this.projectStore.update(project => project.projectTypeId === projectType.id, {
            projectType: null,
            projectTypeId: null
          })
        }
      })
    )
  }

  /**
   * Makes an API call to archive a project type
   * @param {ProjectType} projectType The project type to be archived
   * @returns {Observable<ProjectType>} The project type information
   */
  archiveProjectType(projectType: ProjectType): Observable<ProjectType> {
    return this.apiClient
      .post<ProjectType>(`${this.typeUrl}/${projectType.id}/setarchived`, {
        isArchived: projectType.isArchived
      })
      .pipe(
        map(updatedProjectType => {
          updatedProjectType = new ProjectType(updatedProjectType)
          Object.assign(projectType, updatedProjectType)

          this.projectTypeStore.update(projectType.id, { isArchived: projectType.isArchived })

          return projectType
        })
      )
  }

  // ########### PROJECT ROLES ##############

  /**
   * Makes an API call to request the global project roles
   * @returns {Observable<ProjectRole[]>} The project roles
   */
  fetchProjectRoles(): Observable<ProjectRole[]> {
    return this.apiClient.get<ProjectRole[]>(`${this.roleUrl}`).pipe(
      map(projectRoles => {
        projectRoles = projectRoles.map(projectRole => new ProjectRole(projectRole))

        return this.sortProjectRoles(projectRoles)
      })
    )
  }

  /**
   * Makes an API call to create a global project role
   * @param {ProjectRole} projectRole The project role to be created
   * @returns {Observable<ProjectRole>} The project role information
   */
  sendProjectRole(projectRole: ProjectRole): Observable<ProjectRole> {
    const { name, isDefault, allowOnlyProjectMasterDataRead } = projectRole
    return this.apiClient
      .post<ProjectRole>(`${this.roleUrl}`, { name, isDefault, allowOnlyProjectMasterDataRead })
      .pipe(map(role => new ProjectRole(role)))
  }

  /**
   * Makes an API call to update a global project role
   * @param {ProjectRole} projectRole Project role to be updated
   * @returns {Observable<ProjectRole>} The project role information
   */
  updateProjectRole(projectRole: ProjectRole): Observable<ProjectRole> {
    return this.apiClient
      .put<ProjectRole>(`${this.roleUrl}/${projectRole.id}/`, {
        name: projectRole.name,
        isDefault: projectRole.isDefault,
        allowOnlyProjectMasterDataRead: projectRole.allowOnlyProjectMasterDataRead
      })
      .pipe(map(role => new ProjectRole(role)))
  }

  /**
   * Makes an API call to delete a global project role
   * @param {string} projectRoleId The id of the project role to be deleted
   * @param {IDeleteProjectRoleBody} body
   * @returns {Observable<string>}
   */
  deleteProjectRole(projectRoleId: string, body: IDeleteProjectRoleBody): Observable<string> {
    return this.apiClient.post(`${this.roleUrl}/${projectRoleId}/delete`, body)
  }

  // ########### PROJECT STATUSES ##############

  /**
   * Makes an API call to request project statuses
   * @param {{ projectId?: string; projectTemplateId?: string; }} options
   * @param options.projectId
   * @param options.projectTemplateId
   * @returns {Observable<ProjectStatus[]>} The project statuses
   */
  fetchProjectStatuses(options?: { projectId?: string; projectTemplateId?: string }): Observable<ProjectStatus[]> {
    const requestUrl = options?.projectId ? `${this.url}/${options.projectId}/projectstatuses` : this.statusUrl

    const requestOptions: Options & { apiCache: ApiCacheOptions<ProjectStatus> } = {
      apiCache: this.getProjectStatusCacheScope(options)
    }

    if (!options?.projectId) {
      const queryParams: QueryParams = {
        filterBy: options?.projectTemplateId
          ? `projectTemplateId eq guid'${options.projectTemplateId}'`
          : `isPreset eq false`
      }
      const params: HttpParams = ApiClient.getQueryParams(queryParams)

      requestOptions.params = params
    }

    return this.apiClient.get<ProjectStatus[]>(requestUrl, requestOptions).pipe(
      map(projectStatuses => {
        if (projectStatuses) {
          return projectStatuses.map(projectStatus => new ProjectStatus(projectStatus))
        }

        return projectStatuses
      }),
      tap(projectStatuses => {
        if (options?.projectId) {
          const storedProjectStatuses = this.projectStatusQuery.queryProjectStatusesByProjectId(options.projectId)

          this.projectStatusStore.sync(projectStatuses, storedProjectStatuses())

          return
        }

        if (options?.projectTemplateId) {
          const storedProjectStatuses = this.projectStatusQuery.queryProjectStatusesByProjectTemplateId(
            options.projectTemplateId
          )

          this.projectStatusStore.sync(projectStatuses, storedProjectStatuses())

          return
        }

        const storedProjectStatuses = this.projectStatusQuery.queryNonPresetProjectStatuses()

        this.projectStatusStore.sync(projectStatuses, storedProjectStatuses())
      })
    )
  }

  /**
   * Gets cache scope options for project, template, workflow, or global project status requests.
   * @param {Pick<ProjectStatus, 'projectId' | 'projectTemplateId' | 'workflowId'>} options Status scope data
   * @returns {ApiCacheOptions<ProjectStatus>} The matching project status cache scope
   */
  private getProjectStatusCacheScope(
    options?: Pick<ProjectStatus, 'projectId' | 'projectTemplateId' | 'workflowId'>
  ): ApiCacheOptions<ProjectStatus> {
    if (options?.workflowId) {
      return { entity: ProjectStatus, property: 'workflowId', value: options.workflowId }
    }

    if (options?.projectTemplateId) {
      return { entity: ProjectStatus, property: 'projectTemplateId', value: options.projectTemplateId }
    }

    if (options?.projectId) {
      return { entity: ProjectStatus, property: 'projectId', value: options.projectId }
    }

    return { entity: ProjectStatus, property: 'isPreset', value: false }
  }

  /**
   * Makes an API call to update a global project status
   * @param {ProjectStatus} projectStatus Project status to be updated
   * @returns {Observable<ProjectStatus>} The project status information
   */
  updateProjectStatus(projectStatus: ProjectStatus): Observable<ProjectStatus> {
    return this.apiClient
      .put<ProjectStatus>(
        `${this.statusUrl}/${projectStatus.id}`,
        {
          name: projectStatus.name,
          type: projectStatus.type
        },
        {
          invalidateCache: this.getProjectStatusCacheScope(projectStatus)
        }
      )
      .pipe(
        map(status => new ProjectStatus(status)),
        tap(status => this.projectStatusStore.upsert(status.id, status))
      )
  }

  /**
   * Makes an API call to delete a global project status
   * @param {ProjectStatus} projectStatus The project status to be deleted - Required if is a project's status
   * @param {IDeleteProjectStatusBody} deleteBody
   * @returns {Observable<void>}
   */
  deleteProjectStatus(projectStatus: ProjectStatus, deleteBody?: IDeleteProjectStatusBody): Observable<void> {
    return this.apiClient
      .post<void>(`${this.statusUrl}/${projectStatus.id}/delete`, deleteBody, {
        invalidateCache: this.getProjectStatusCacheScope(projectStatus)
      })
      .pipe(tap(() => this.projectStatusStore.remove(projectStatus.id)))
  }

  /**
   * Sets a new status to the project
   * @param {Project} project The project whose status is to be changed
   * @param {ProjectStatus} newStatus The new project status
   * @returns {Observable<Project>} The current project with the saved status
   */
  setProjectStatus(project: Project, newStatus: ProjectStatus): Observable<Project> {
    return this.apiClient
      .post<void>(`${this.url}/${project.id}/changeStatus`, {
        projectStatusId: newStatus.id
      })
      .pipe(
        map(() => {
          this.projectStore.update(project.id, {
            projectStatus: newStatus,
            projectStatusId: newStatus.id
          })

          const updatedProject =
            this.projectQuery.getProject(project.id) ??
            new Project({ ...project, projectStatus: newStatus, projectStatusId: newStatus.id })

          if (newStatus.type === 'closed') {
            this.projectClosed.next(updatedProject)
          }

          this.projectUpdated.next(updatedProject)

          return updatedProject
        }),
        catchError(error => {
          return throwError(error)
        })
      )
  }

  // ########### PROJECT TEMPLATE ##############

  /**
   * Makes an API call to fetch all the project templates
   * @param {QueryParams} queryParams
   * @returns {Observable<ProjectTemplate[]>}
   */
  fetchProjectTemplates(queryParams?: QueryParams): Observable<ProjectTemplate[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)
    return this.apiClient.get<ProjectTemplate[]>(this.templateUrl, { params }).pipe(
      map(projectTemplates => {
        if (projectTemplates) {
          projectTemplates = projectTemplates.map(projectTemplate => new ProjectTemplate(projectTemplate))
        }

        return projectTemplates
      }),
      tap(projectTemplates => {
        this.projectTemplateStore.set(projectTemplates)
      })
    )
  }

  /**
   * Makes an API call to request the total count of project templates
   * @param {QueryParams} queryParams
   * @returns {Observable<number>} Observable of a number
   */
  fetchProjectTemplateCount(queryParams?: QueryParams): Observable<number> {
    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    params = params.append('count', 'true')

    const call$ = this.apiClient.get<HttpResponse<Project[]>>(this.templateUrl, {
      params,
      observe: 'response' // To get the full response and get access to the headers (pagination info)
    })

    return call$.pipe(
      map((res: HttpResponse<Project[]>) => {
        return Number(res.headers.get('aw-totalitems'))
      })
    )
  }

  /**
   * Makes an API to fetch a project template
   * @param {string} projectTemplateId
   * @returns {Observable<ProjectTemplate>}
   */
  fetchProjectTemplate(projectTemplateId: string): Observable<ProjectTemplate> {
    return this.apiClient.get<ProjectTemplate>(`${this.templateUrl}/${projectTemplateId}`).pipe(
      map(projectTemplate => new ProjectTemplate(projectTemplate)),
      tap(projectTemplate => {
        this.projectTemplateStore.upsert(projectTemplate.id, projectTemplate)
      })
    )
  }

  /**
   * Makes an API call to create a project template
   * @param {ProjectTemplate} projectTemplate The project template to be created
   * @returns {Observable<ProjectTemplate>} The project template information
   */
  sendProjectTemplate(projectTemplate: ProjectTemplate): Observable<ProjectTemplate> {
    return this.apiClient
      .post<ProjectTemplate>(`${this.templateUrl}`, {
        name: projectTemplate.name,
        description: projectTemplate.description,
        tags: projectTemplate.tags
      })
      .pipe(
        map(newProjectTemplate => {
          newProjectTemplate = new ProjectTemplate(newProjectTemplate)

          return newProjectTemplate
        }),
        tap(newProjectTemplate => {
          this.projectTemplateStore.add(newProjectTemplate)
        })
      )
  }

  /**
   * Makes an API call to duplicate a project template
   * @param {ProjectTemplate} projectTemplate The project template to be duplicated
   * @returns {Observable<ProjectTemplate>} The project template information
   */
  duplicateProjectTemplate(projectTemplate: ProjectTemplate): Observable<ProjectTemplate> {
    return this.apiClient.post<ProjectTemplate>(`${this.templateUrl}/${projectTemplate.id}/copy`, {}).pipe(
      map(newProjectTemplate => {
        newProjectTemplate = new ProjectTemplate(newProjectTemplate)

        return newProjectTemplate
      }),
      tap(newProjectTemplate => {
        this.projectTemplateStore.add(newProjectTemplate)
      })
    )
  }

  /**
   * Makes an API call to update a project template
   * @param {ProjectTemplate} projectTemplate The project template to be updated
   * @returns {Observable<ProjectTemplate>} The project template information
   */
  updateProjectTemplate(projectTemplate: ProjectTemplate): Observable<ProjectTemplate> {
    return this.apiClient
      .put<ProjectTemplate>(`${this.templateUrl}/${projectTemplate.id}`, {
        name: projectTemplate.name,
        description: projectTemplate.description,
        tags: projectTemplate.tags,
        isBillableByDefault: projectTemplate.isBillableByDefault,
        defaultTaskListIdForEmails: projectTemplate.defaultTaskListIdForEmails,
        projectTypeId: projectTemplate.projectTypeId,
        timeBudget: projectTemplate.timeBudget,
        copyImageToProjectOnCreation: projectTemplate.copyImageToProjectOnCreation
      } as Partial<ProjectTemplate>)
      .pipe(
        map(updatedProjectTemplate => {
          updatedProjectTemplate = new ProjectTemplate(updatedProjectTemplate)
          Object.assign(projectTemplate, updatedProjectTemplate)

          return projectTemplate
        }),
        tap(updatedProjectTemplate => {
          this.projectTemplateStore.update(updatedProjectTemplate.id, updatedProjectTemplate)
        })
      )
  }

  /**
   * Makes an API call to delete a project template
   * @param {string} projectTemplateId The project template to be deleted
   * @returns {Observable<void>}
   */
  deleteProjectTemplate(projectTemplateId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.templateUrl}/${projectTemplateId}`).pipe(
      tap(() => {
        this.projectTemplateStore.remove(projectTemplateId)
      }),
      // Finish store reconciliation if the delete dialog closes during the request.
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }

  /**
   * Links a workflow to a project template
   * @param {string} projectTemplateId The project template to link the workflow to
   * @param {LinkWorkflowForm} form The form containing workflow ID and optional status mappings
   * @returns {Observable<ProjectTemplate>} The updated project template
   */
  linkWorkflowToProjectTemplate(projectTemplateId: string, form: LinkWorkflowForm): Observable<ProjectTemplate> {
    return this.apiClient.post<ProjectTemplate>(`${this.templateUrl}/${projectTemplateId}/linkworkflow`, form).pipe(
      map(projectTemplate => {
        projectTemplate = new ProjectTemplate(projectTemplate)

        return projectTemplate
      }),
      tap(projectTemplate => {
        this.projectTemplateStore.update(projectTemplateId, projectTemplate)
      })
    )
  }

  /**
   * Unlinks a workflow from a project template
   * @param {string} projectTemplateId The project template to unlink the workflow from
   * @returns {Observable<ProjectTemplate>} The updated project template
   */
  unlinkWorkflowFromProjectTemplate(projectTemplateId: string): Observable<ProjectTemplate> {
    return this.apiClient.post<ProjectTemplate>(`${this.templateUrl}/${projectTemplateId}/unlinkworkflow`, {}).pipe(
      map(projectTemplate => new ProjectTemplate(projectTemplate)),
      tap(projectTemplate => {
        this.projectTemplateStore.update(projectTemplateId, projectTemplate)
      })
    )
  }

  /**
   * Links a workflow to a project
   * @param {string} projectId The project to link the workflow to
   * @param {LinkWorkflowForm} form The form containing workflow ID and status mappings
   * @returns {Observable<Project>} The updated project
   */
  linkWorkflowToProject(projectId: string, form: LinkWorkflowForm): Observable<Project> {
    return this.apiClient.post<Project>(`${this.url}/${projectId}/linkworkflow`, form).pipe(
      map(project => new Project(project)),
      tap(project => {
        this.projectStore.update(projectId, project)
      })
    )
  }

  /**
   * Unlinks a workflow from a project
   * @param {string} projectId The project to unlink the workflow from
   * @returns {Observable<Project>} The updated project
   */
  unlinkWorkflowFromProject(projectId: string): Observable<Project> {
    return this.apiClient.post<Project>(`${this.url}/${projectId}/unlinkworkflow`, {}).pipe(
      map(project => new Project({ ...project, workflowId: null })),
      tap(project => {
        this.projectStore.update(projectId, project)
      })
    )
  }

  /**
   * Makes an API to fetch the project template's task bundle id
   * @param {string} projectTemplateId
   * @returns {Observable<{ taskBundleId: string; }>}
   */
  fetchProjectTemplateTaskBundleId(projectTemplateId: string): Observable<{ taskBundleId: string }> {
    return this.apiClient.get<{ taskBundleId: string }>(`${this.templateUrl}/${projectTemplateId}/taskbundle`)
  }

  /**
   * Create project template from project
   * @param {Project} project
   * @param {ProjectTemplateImportOptions} importedData
   * @returns {Observable<ProjectTemplate>}
   */
  createProjectTemplateFromProject(
    project: Project,
    importedData: ProjectTemplateImportOptions
  ): Observable<ProjectTemplate> {
    return this.apiClient
      .post<ProjectTemplate>(`${this.templateUrl}/fromProject/${project.id}`, importedData)
      .pipe(map(projectTemplate => new ProjectTemplate(projectTemplate)))
  }

  // ########### PUBLIC PROJECT TEMPLATE ##############

  /**
   * Makes an API call to fetch all the public project templates
   * @param {QueryParams} queryParams
   * @returns {Observable<PublicProjectTemplate[]>}
   */
  fetchPublicProjectTemplates(queryParams: QueryParams): Observable<PublicProjectTemplate[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .get<PublicProjectTemplate[]>(this.publicTemplateUrl, { params })
      .pipe(map(templates => templates.map(t => new PublicProjectTemplate(t))))
  }

  /**
   * Makes an API to fetch a public project template
   * @param {string} templateId
   * @returns {Observable<PublicProjectTemplate>}
   */
  fetchPublicProjectTemplate(templateId: string): Observable<PublicProjectTemplate> {
    return this.apiClient
      .get<PublicProjectTemplate>(`${this.publicTemplateUrl}/${templateId}`)
      .pipe(map(template => new PublicProjectTemplate(template)))
  }

  // ########### PROJECT TEMPLATE STATUSES ##############

  /**
   * Makes an API call to create a project template status
   * @param {ProjectStatus} projectStatus
   * @param {{ projectId?: string; projectTemplateId?: string; }} options
   * @param options.projectId
   * @param options.projectTemplateId
   * @returns {Observable<ProjectStatus>}
   */
  sendProjectStatus(
    projectStatus: ProjectStatus,
    options: { projectId?: string; projectTemplateId?: string }
  ): Observable<ProjectStatus> {
    const url = options.projectId
      ? `${this.url}/${options.projectId}`
      : `${this.templateUrl}/${options.projectTemplateId}`

    return this.apiClient
      .post<ProjectStatus>(
        `${url}/projectstatuses`,
        {
          name: projectStatus.name,
          type: projectStatus.type
        },
        {
          invalidateCache: this.getProjectStatusCacheScope(options)
        }
      )
      .pipe(
        map(status => new ProjectStatus(status)),
        tap(status => this.projectStatusStore.add(status))
      )
  }

  /**
   * Saves a project template status order and syncs the saved statuses.
   * @param {ProjectTemplate} projectTemplate Project template to be updated
   * @param {ProjectStatus} projectStatus Project status to be updated
   * @returns {Observable<ProjectStatus[]>} The saved project statuses
   */
  updateProjectTemplateStatus(
    projectTemplate: ProjectTemplate,
    projectStatus: ProjectStatus
  ): Observable<ProjectStatus[]> {
    return this.apiClient
      .post<void>(
        `${this.templateUrl}/${projectTemplate.id}/updateprojectstatusorder`,
        {
          projectStatusId: projectStatus.id,
          order: projectStatus.order
        },
        {
          invalidateCache: this.getProjectStatusCacheScope({ projectTemplateId: projectTemplate.id })
        }
      )
      .pipe(
        switchMap(() =>
          this.fetchProjectStatuses({ projectTemplateId: projectTemplate.id }).pipe(catchError(() => EMPTY))
        ),
        tap(savedStatuses => this.updateProjectStatusesInStore(savedStatuses))
      )
  }

  /**
   * Saves a project status order and syncs the saved statuses.
   * @param {Project} project Project to be updated
   * @param {ProjectStatus} projectStatus Project status to be updated
   * @returns {Observable<ProjectStatus[]>} The saved project statuses
   */
  updateProjectStatusOrder(project: Project, projectStatus: ProjectStatus): Observable<ProjectStatus[]> {
    return this.apiClient
      .post<void>(
        `${this.url}/${project.id}/updateprojectstatusorder`,
        {
          projectStatusId: projectStatus.id,
          order: projectStatus.order
        },
        {
          invalidateCache: this.getProjectStatusCacheScope({ projectId: project.id })
        }
      )
      .pipe(
        switchMap(() => this.fetchProjectStatuses({ projectId: project.id }).pipe(catchError(() => EMPTY))),
        tap(savedStatuses => this.updateProjectStatusesInStore(savedStatuses))
      )
  }

  /**
   * Updates project statuses in the store as one state change.
   * @param {ProjectStatus[]} projectStatuses Project statuses to update
   */
  updateProjectStatusesInStore(projectStatuses: ProjectStatus[]): void {
    const projectStatusesById = new Map(projectStatuses.map(projectStatus => [projectStatus.id, projectStatus]))
    this.projectStatusStore.update(
      projectStatuses.map(projectStatus => projectStatus.id),
      storedProjectStatus => projectStatusesById.get(storedProjectStatus.id) ?? storedProjectStatus
    )
  }

  // ########### PROJECT TAGS ##############

  /**
   * Makes an API call to get the tags available for projects and templates
   * @param {boolean} includeTemplateTags
   * @returns {Observable<Tag[]>}
   */
  fetchTags(includeTemplateTags?: boolean): Observable<Tag[]> {
    if (includeTemplateTags) {
      return this.fetchTemplateTags()
    }

    return this.apiClient.get<Tag[]>(`${this.url}/tags`).pipe(map(this.mapLegacyTags))
  }

  /**
   * Makes an API call to get the tags available for project templates
   * @returns {Observable<Tag[]>}
   */
  fetchTemplateTags(): Observable<Tag[]> {
    return forkJoin({
      projectTags: this.apiClient.get<Tag[]>(`${this.url}/tags`),
      templateTags: this.apiClient.get<Tag[]>(`${this.templateUrl}/tags`)
    }).pipe(
      map(({ templateTags, projectTags }) => {
        const allTags = uniqueBy([...templateTags, ...projectTags], 'name')

        return this.mapLegacyTags(allTags)
      })
    )
  }

  /**
   * Maps legacy string maps to Tag objects
   * TODO: Check if migration is done to remove this mapping
   * @param {Tag[]} tags
   * @returns {Tag[]}
   */
  private mapLegacyTags(tags: Tag[]): Tag[] {
    if (typeof tags?.[0] === 'string') {
      return tags.map(tag => ({ name: tag as unknown as string }))
    } else {
      return tags
    }
  }

  /**
   * Makes an API call to get the tags available for projects templates
   * @param {string} projectTemplateId
   * @returns {Observable<Tag[]>}
   */
  fetchProjectTemplateTags(projectTemplateId: string): Observable<Tag[]> {
    return this.apiClient.get<Tag[]>(`${this.templateUrl}/${projectTemplateId}/tags`).pipe(
      tap(tags => {
        this.projectTemplateStore.update(projectTemplateId, { tags })
      })
    )
  }

  /**
   * Makes API calls to add/delete tags
   * @param {TagOwner} owner The project or project template to be updated
   * @param {Tag[]} tags The project's tags
   * @param {Tag[]} addedTags The tags to be added to the project
   * @param {Tag[]} removedTags The tags to be deleted from the project
   * @returns {Observable<Tag[]>}
   */
  manageTags(owner: TagOwner, tags: Tag[], addedTags: Tag[], removedTags: Tag[]): Observable<Tag[]> {
    const calls$: Observable<void>[] = []
    const entity = owner as Project | ProjectTemplate

    if (addedTags.length) {
      calls$.push(this.sendTags(entity, addedTags))
    }

    if (removedTags.length) {
      calls$.push(this.deleteTags(entity, removedTags))
    }

    return forkJoin(calls$).pipe(
      map(() => {
        entity.tags = tags

        if (entity instanceof Project) {
          this.projectStore.update(entity.id, { tags })
        }

        return tags
      })
    )
  }

  /**
   * Makes an API call to add tags to the project
   * @param {Project | ProjectTemplate} entity The project or project template to be updated
   * @param {Tag[]} tags The tags to be added to the project
   * @returns {Observable<void>}
   */
  private sendTags(entity: Project | ProjectTemplate, tags: Tag[]): Observable<void> {
    const url = entity instanceof Project ? this.url : this.templateUrl

    return this.apiClient.post<void>(`${url}/${entity.id}/addtags`, tags, {
      entityName: 'project',
      propertyName: 'tags'
    })
  }

  /**
   * Makes an API call to delete tags from the project
   * @param {Project | ProjectTemplate} entity The project or project template to be updated
   * @param {Tag[]} tags The tags to be deleted from the project
   * @returns {Observable<void>}
   */
  private deleteTags(entity: Project | ProjectTemplate, tags: Tag[]): Observable<void> {
    const url = entity instanceof Project ? this.url : this.templateUrl

    return this.apiClient.post<void>(`${url}/${entity.id}/deletetags`, tags, {
      entityName: 'project',
      propertyName: 'tags'
    })
  }

  /**
   * Makes an API call to add tags to the project templates
   * @param {ProjectTemplate} projectTemplate
   * @param {Tag[]} tags
   * @returns {Observable<void>}
   * @private
   */
  private sendProjectTemplateTags(projectTemplate: ProjectTemplate, tags: Tag[]): Observable<void> {
    return this.apiClient.post<void>(`${this.templateUrl}/${projectTemplate.id}/addtags`, tags)
  }

  /**
   * Makes an API call to delete tags from the project
   * @param {ProjectTemplate} projectTemplate
   * @param {Tag[]} tags
   * @returns {Observable<void>}
   * @private
   */
  private deleteProjectTemplateTags(projectTemplate: ProjectTemplate, tags: Tag[]): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/${projectTemplate.id}/deletetags`, tags)
  }

  /**
   * Makes an API call to delete the tag for all projects
   * @param {Tag} tag The tag to be deleted
   * @returns {Observable<void>}
   */
  deleteTagGlobally(tag: Tag): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/deletetags`, tag)
  }

  /**
   * Makes an API call to update the tag for all projects
   * @param {Tag} tag
   * @param {Tag} oldTag
   * @param {boolean} shouldMerge
   * @returns {Observable<Tag>}
   */
  updateTag(tag: Tag, oldTag: Tag, shouldMerge?: boolean): Observable<Tag> {
    return this.apiClient.post<Tag>(`${this.url}/updatetags`, {
      oldTagName: oldTag.name,
      newTag: tag,
      shouldMerge
    })
  }

  /**
   * Makes an API call to update the tag for all project templates
   * @param {Tag} tag
   * @param {Tag} oldTag
   * @param {boolean} shouldMerge
   * @returns {Observable<Tag>}
   */
  updateProjectTemplateTag(tag: Tag, oldTag: Tag, shouldMerge?: boolean): Observable<Tag> {
    return this.apiClient.post<Tag>(`${this.templateUrl}/updatetags`, {
      oldTagName: oldTag.name,
      newTag: tag,
      shouldMerge
    })
  }

  // ########### TASK BUNDLES FOR PROJECT TYPE ##############

  /**
   * Makes an API call to get all the task bundle ids linked to a project type
   * @param {string} projectTypeId The id of the project type
   * @param {boolean} [showArchived=false] Flag to show the task bundles of an archived project type
   * @returns {Observable<ProjectTypeTaskBundles>}
   */
  fetchTaskBundleIdsForProjectType(projectTypeId: string, showArchived = false): Observable<ProjectTypeTaskBundles> {
    const params = new HttpParams({ fromObject: { showArchived: showArchived ? 'true' : 'false' } })
    return this.apiClient.get<ProjectTypeTaskBundles>(`${this.typeUrl}/${projectTypeId}/taskbundles`, { params })
  }

  /**
   * Makes an API call to link a task bundle to a project type
   * @param {string} projectTypeId The id of the project type
   * @param {TaskBundle} taskBundle The task bundle that is to be linked
   * @returns {Observable<string>}
   */
  linkTaskBundleToProjectType(projectTypeId: string, taskBundle: TaskBundle): Observable<string> {
    return this.apiClient.post<string>(`${this.typeUrl}/${projectTypeId}/linktaskbundle`, {
      taskBundleId: taskBundle.id
    })
  }

  /**
   * Makes an API call to unlink a task bundle from a project type
   * @param {TaskBundleAssignment} taskBundleAssignment The task bundle to be unlinked
   * @returns {Observable<string>}
   */
  unlinkTaskBundleFromProjectType(taskBundleAssignment: TaskBundleAssignment): Observable<string> {
    return this.apiClient.post<string>(`${this.typeUrl}/${taskBundleAssignment.projectType.id}/unlinktaskbundle`, {
      taskBundleId: taskBundleAssignment.taskBundle.id
    })
  }

  // ########### TASK BUNDLES FOR PROJECT STATUS ##############

  /**
   * Makes an API call to link a task bundle to a project status
   * @param {string} projectTypeId The id of the project type
   * @param {string} projectStatusId The id of the project status
   * @param {TaskBundle} taskBundle The task bundle that is to be linked
   * @returns {Observable<string>}
   */
  linkTaskBundleToProjectStatus(
    projectTypeId: string,
    projectStatusId: string,
    taskBundle: TaskBundle
  ): Observable<string> {
    return this.apiClient.post<string>(
      `${this.typeUrl}/${projectTypeId}/projectstatuses/${projectStatusId}/linktaskbundle`,
      {
        taskBundleId: taskBundle.id
      }
    )
  }

  /**
   * Makes an API call to unlink a task bundle from a project status
   * @param {TaskBundleAssignment} taskBundleAssignment The task bundle to be unlinked
   * @returns {Observable<string>}
   */
  unlinkTaskBundleFromProjectStatus(taskBundleAssignment: TaskBundleAssignment): Observable<string> {
    return this.apiClient.post<string>(
      `${this.typeUrl}/${taskBundleAssignment.projectType.id}/projectstatuses/${taskBundleAssignment.projectStatus.id}/unlinktaskbundle`,
      {
        taskBundleId: taskBundleAssignment.taskBundle.id
      }
    )
  }

  // ########### PROJECT COUNT ##############

  /**
   * Makes an API call to request the total count of projects for a given filter
   * @param {QueryParams} queryParams
   * @returns {Observable<number>} Observable of a number
   */
  fetchProjectCount(queryParams?: QueryParams): Observable<number> {
    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    params = params.append('count', 'true')

    const call$ = this.apiClient.get<HttpResponse<Project[]>>(this.url, {
      params,
      observe: 'response' // To get the full response and get access to the headers (pagination info)
    })

    return call$.pipe(
      map((res: HttpResponse<Project[]>) => {
        return Number(res.headers.get('aw-totalitems'))
      })
    )
  }

  /**
   * Makes an API call to add a project role to a project type
   * @param {string} projectTypeId Project Migration object
   * @param {string} projectStatusId
   * @param {IRemoveProjectRoleBody[]} projectRoleChanges
   * @param {Project} project Edited Project
   * @returns {Observable<void>}
   */
  migrateProject(
    projectTypeId: string,
    projectStatusId: string,
    projectRoleChanges: IRemoveProjectRoleBody[],
    project: Project
  ): Observable<void> {
    const body = { projectTypeId, projectStatusId, projectRoleChanges }

    return this.apiClient.post<void>(`${this.url}/${project.id}/changeprojecttype/`, body).pipe(
      map(() => {
        this.projectStore.update(project.id, project)
      })
    )
  }

  /**
   * Sets the id of the active project
   * @param {string} id
   */
  setActiveProject(id: string): void {
    this.projectStore.setActive(id)
  }

  // ########### PROJECT MILESTONES ##############

  /**
   * Fetches the project's milestones
   * @param {string} projectId
   * @returns {Observable<ProjectMilestone[]>}
   */
  fetchProjectMilestones(projectId: string): Observable<ProjectMilestone[]> {
    return this.apiClient.get<ProjectMilestone[]>(`${this.url}/${projectId}/milestones`).pipe(
      map(milestones => {
        milestones = milestones.map(milestone => new ProjectMilestone(milestone))

        this.projectStore.update(projectId, { milestones })

        return milestones
      })
    )
  }

  /**
   * Fetches the milestones of all the specified projects
   * @param {string[]} projectIds
   * @returns {Observable<ProjectMilestone[]>}
   */
  fetchProjectsMilestones(projectIds: string[]): Observable<ProjectMilestone[]> {
    const params: HttpParams = ApiClient.getQueryParams({})

    return this.apiClient.getAll<ProjectMilestone[]>(`${apiEndpoint}/projectmilestones`, { params }).pipe(
      map(response => {
        const milestones =
          response?.reduce((acc: ProjectMilestone[], milestone: ProjectMilestone) => {
            if (projectIds.includes(milestone.projectId)) {
              acc.push(new ProjectMilestone(milestone))
            }

            return acc
          }, []) || []

        const groupedMilestones = groupBy(milestones, milestone => milestone.projectId)

        projectIds.forEach(projectId => {
          this.projectStore.update(projectId, { milestones: groupedMilestones[projectId] || [] })
        })

        return milestones
      })
    )
  }

  /**
   * Transform the date to
   * @param {Date | string} date
   * @returns {string}
   */
  private getDateString(date: Date | string): string {
    return format(date, 'yyyy-MM-dd')
  }

  /**
   * Sends the new project's milestone
   * @param {string} projectId
   * @param {ProjectMilestone} milestone
   * @returns {Observable<ProjectMilestone>}
   */
  sendProjectMilestone(projectId: string, milestone: ProjectMilestone): Observable<ProjectMilestone> {
    const optimisticId = generateGUID()

    this.projectStore.update(projectId, project => ({
      milestones: arrayAdd(project.milestones, new ProjectMilestone({ ...milestone, id: optimisticId }))
    }))

    milestone.dueDate = this.getDateString(milestone.dueDate as Date)

    return this.apiClient
      .post<ProjectMilestone>(this.milestonesUrl, {
        ...milestone,
        projectId
      })
      .pipe(
        map(newMilestone => {
          newMilestone = new ProjectMilestone(newMilestone)

          this.projectStore.update(projectId, project => ({
            milestones: arrayRemove(project.milestones, optimisticId)
          }))

          this.projectStore.update(projectId, project => ({
            milestones: arrayAdd(project.milestones, newMilestone)
          }))

          return newMilestone
        }),
        catchError(error => {
          this.projectStore.update(projectId, project => ({
            milestones: arrayRemove(project.milestones, optimisticId)
          }))

          return throwError(error)
        }),
        // Finish store reconciliation even if the editor closes during the request.
        shareReplay({ bufferSize: 1, refCount: false })
      )
  }

  /**
   * Updates the project's milestone
   * @param {string} projectId
   * @param {ProjectMilestone} milestone
   * @returns {Observable<ProjectMilestone>}
   */
  updateProjectMilestone(projectId: string, milestone: ProjectMilestone): Observable<ProjectMilestone> {
    this.projectStore.update(projectId, project => ({
      milestones: arrayUpdate(project.milestones, milestone.id, milestone)
    }))

    milestone.dueDate = this.getDateString(milestone.dueDate as Date)

    return this.apiClient.put<ProjectMilestone>(`${this.milestonesUrl}/${milestone.id}`, milestone).pipe(
      catchError(error => {
        this.projectStore.update(projectId, project => ({
          milestones: arrayRemove(project.milestones, milestone.id)
        }))

        return throwError(error)
      }),
      // Finish the optimistic write even if the editor closes during the request.
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }

  /**
   * Deletes the project's milestone
   * @param {string} projectId
   * @param {ProjectMilestone} milestone
   * @returns {Observable<void>}
   */
  deleteProjectMilestone(projectId: string, milestone: ProjectMilestone): Observable<void> {
    this.projectStore.update(projectId, project => ({
      milestones: arrayRemove(project.milestones, milestone.id)
    }))

    return this.apiClient.delete<void>(`${this.milestonesUrl}/${milestone.id}`).pipe(
      catchError(error => {
        this.projectStore.update(projectId, project => ({
          milestones: arrayAdd(project.milestones, milestone)
        }))

        return throwError(error)
      })
    )
  }

  /**
   * Sort the project roles alphabetically with the exception case to the end
   * @param {ProjectRole[]} projectRoles
   * @returns {ProjectRole[]}
   */
  private sortProjectRoles(projectRoles: ProjectRole[]): ProjectRole[] {
    const exceptions = ['external', 'extern']

    return projectRoles.sort((a, b) => {
      const indexA = exceptions.indexOf(a.name.toLowerCase())
      const indexB = exceptions.indexOf(b.name.toLowerCase())

      const nameA = a.name.toLowerCase()
      const nameB = b.name.toLowerCase()

      // Regular case
      if (indexA === -1 && indexB === -1) {
        return nameA.localeCompare(nameB)
      }

      // Exception case
      return indexA - indexB
    })
  }

  /**
   * Fetch Project Autopilot
   * @param {string} projectId
   * @returns {Observable<Autopilot>}
   */
  fetchProjectAutopilot(projectId: string): Observable<Autopilot> {
    return this.apiClient.get<Autopilot>(`${this.url}/${projectId}/autopilot`)
  }

  /**
   * Send Project Autopilot type
   * @param {string} projectId
   * @param {AutopilotType} autopilotType 'agile' | 'waterfall'
   * @param {AutopilotAlert[]} alerts
   * @returns {Observable<Autopilot>}
   */
  sendProjectAutopilotType(
    projectId: string,
    autopilotType: AutopilotType,
    alerts?: AutopilotAlert[]
  ): Observable<Autopilot> {
    const autopilotBody = {
      type: autopilotType,
      alerts
    }
    return this.apiClient.post<Autopilot>(`${this.url}/${projectId}/autopilot`, autopilotBody)
  }

  /**
   * Update prohect autopilot Settings
   * @param {string} projectId
   * @param {Autopilot} autopilot
   * @returns {Observable<Autopilot>}
   */
  updateProjectAutopilotSettings(projectId: string, autopilot: Autopilot): Observable<Autopilot> {
    return this.apiClient.put<Autopilot>(`${this.url}/${projectId}/autopilot`, autopilot)
  }
  /**
   * Delete Autopilot from project
   * @param {string} projectId
   * @returns {Observable<void>}
   */
  deleteProjectAutopilot(projectId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.url}/${projectId}/autopilot`)
  }

  /**
   * Fetch all available alerts
   * @returns {Observable<string[]>}
   */
  fetchAutopilotAvailableAlerts(): Observable<string[]> {
    return this.apiClient.get<string[]>(`${this.autopilotUrl}/availablealerts`)
  }

  fetchDefaultTaskListId(projectId: string): Observable<string> {
    return this.apiClient
      .get<{ defaultTasklistId: string }>(`${this.url}/${projectId}/defaulttasklistid`)
      .pipe(map(response => response.defaultTasklistId))
  }

  updateDefaultTaskListId(projectId: string, taskListId: string): Observable<string> {
    return this.apiClient
      .post<{ defaultTasklistId: string }>(`${this.url}/${projectId}/setdefaulttasklistid`, {
        defaultTasklistId: taskListId
      })
      .pipe(map(response => response.defaultTasklistId))
  }

  /**
   * Sends a project connection (invitation) to an external user to join the project
   * @param {string} projectId
   * @param {string} externalUserEmail
   * @param {string} projectRoleId
   * @returns {Observable<void>}
   */
  sendProjectConnection(projectId: string, externalUserEmail: string, projectRoleId: string): Observable<void> {
    return this.apiClient.post<void>(`${this.projectConnectionsUrl}`, {
      projectId,
      externalUserEmail,
      projectRoleId
    })
  }

  /**
   * Updates the projectRoleId of a project connection (invitation)
   * @param {string} projectConnectionId
   * @param {string} projectRoleId
   * @returns {Observable<void>}
   */
  updateProjectConnectionProjectRole(projectConnectionId: string, projectRoleId: string): Observable<void> {
    return this.apiClient.post<void>(`${this.projectConnectionsUrl}/${projectConnectionId}/updateProjectRole`, {
      projectRoleId
    })
  }

  /**
   * Deletes the project connection for a project by id
   * @param {string} projectId
   * @param {string} connectionId
   * @returns {Observable<void>}
   */
  deleteProjectConnection(projectId: string, connectionId: string): Observable<void> {
    return this.apiClient.delete(`${this.url}/${projectId}/connections/${connectionId}`)
  }

  /**
   * Gets the project connections for a project
   * @param {string} projectId
   * @returns {Observable<ProjectConnectionInvite[]>}
   */
  fetchProjectConnections(projectId: string): Observable<ProjectConnectionInvite[]> {
    return this.apiClient.get<ProjectConnectionInvite[]>(`${this.url}/${projectId}/connections`)
  }

  /**
   * Fetches all accepted project connections for the current workspace
   * @returns {Observable<AcceptedProjectConnection[]>}
   */
  fetchAcceptedProjectConnections(): Observable<AcceptedProjectConnection[]> {
    return this.apiClient.get<AcceptedProjectConnection[]>(this.projectConnectionsUrl)
  }

  /**
   * Validates the project connection invitation code
   * @param {string} inviteCode
   * @returns {Observable<void>}
   */
  validateProjectConnectionInviteCode(inviteCode: string): Observable<void> {
    return this.apiClient.post<void>(`${this.projectConnectionsUrl}/validatecode`, { inviteCode })
  }

  /**
   * Makes an API call to accept a project connection and join the external project
   * in the destination workspace
   * @param {string} inviteCode
   * @param {string} externalWorkspaceId
   * @returns {Observable<string>}
   */
  acceptProjectConnection(inviteCode: string, externalWorkspaceId: string): Observable<string> {
    return this.apiClient
      .post<ProjectConnectionInvite>(`${this.projectConnectionsUrl}/accept`, { inviteCode, externalWorkspaceId })
      .pipe(map(response => response.projectId))
  }

  /**
   * Returns the public information of the project connection available through the invitation code
   * @param {string} inviteCode
   * @returns {Observable<ProjectConnectionInformation>}
   */
  fetchProjectConnectionInformation(inviteCode: string): Observable<ProjectConnectionInformation> {
    return this.apiClient
      .get<ProjectConnectionInformation>(`${this.projectConnectionsUrl}/bycode/${inviteCode}`)
      .pipe(map(projectInformation => this.mapProjectConnectionInformation(projectInformation)))
  }

  /**
   * Gets the project connection status. The status is used to identify if the current
   * workspace is connected to the project and if the connection is read-only, meaning
   * that the subscription of the home workspace is expired.
   * @param {string} projectId
   * @returns {Observable<ProjectConnectionStatus>}
   */
  fetchProjectConnectionStatus(projectId: string): Observable<ProjectConnectionStatus> {
    return this.apiClient.get<ProjectConnectionStatus>(`${this.url}/${projectId}/connectionStatus`)
  }

  /**
   * Map External Project Information by adding data necessary to render workspace and user avatars
   * @param {ProjectConnectionInformation} projectInformation
   * @returns {ProjectConnectionInformation}
   * @private
   */
  private mapProjectConnectionInformation(
    projectInformation: ProjectConnectionInformation
  ): ProjectConnectionInformation {
    // TODO: Implement source user id once is available in the backend
    projectInformation.homeUserAvatarEntity = new User(
      {
        id: generateGUID(),
        firstName: projectInformation.homeUserFullName,
        hasImage: true,
        publicImageToken: projectInformation.homeUserPublicImageToken
      },
      'ProjectService'
    )

    projectInformation.homeWorkspaceAvatarEntity = new Workspace({
      id: projectInformation.homeWorkspaceId,
      name: projectInformation.homeWorkspaceName,
      hasImage: true,
      publicImageToken: projectInformation.homeWorkspacePublicImageToken,

      // Mock data as this is not relevant to render workspace avatar
      description: '',
      language: 'en',
      size: 'small',
      type: 'private'
    })

    return projectInformation
  }

  /**
   * Migrates the guest user to its own workspace
   * @param {string} targetWorkspaceId
   * @returns {Observable<Workspace>}
   */
  migrateGuestUser(targetWorkspaceId?: string): Observable<Workspace> {
    const form: { targetWorkspaceId?: string } = {}

    if (targetWorkspaceId) {
      form.targetWorkspaceId = targetWorkspaceId
    }

    return this.apiClient
      .post<Workspace>(`${this.projectConnectionsUrl}/migrateguest`, form)
      .pipe(map(workspace => new Workspace(workspace)))
  }

  /**
   * Fetches all trusted project connections for the workspace
   * @returns {Observable<TrustedProjectConnection[]>}
   */
  getTrustedProjectConnections(): Observable<TrustedProjectConnection[]> {
    return this.apiClient.get<TrustedProjectConnection[]>(this.trustedProjectConnectionsUrl)
  }

  /**
   * Creates a trusted project connection
   * @param {TrustedProjectConnectionForm} connectionData
   * @returns {Observable<TrustedProjectConnection>}
   */
  createTrustedProjectConnection(connectionData: TrustedProjectConnectionForm): Observable<TrustedProjectConnection> {
    return this.apiClient.post<TrustedProjectConnection>(this.trustedProjectConnectionsUrl, connectionData)
  }

  /**
   * Deletes a trusted project connection
   * @param {string} id
   * @returns {Observable<void>}
   */
  deleteTrustedProjectConnection(id: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.trustedProjectConnectionsUrl}/${id}`)
  }

  /**
   * Builds filter expression based on the entity filter and the selected filter
   * @param {{ searchQuery?: string; entityFilter?: string; filterBy?: string; quickFilter?: QuickFilter; dateRange?: DateRange; }} filters
   * @param filters.searchQuery
   * @param filters.entityFilter
   * @param filters.filterBy
   * @param filters.quickFilter
   * @param filters.dateRange
   * @returns {string}
   */
  buildFilterExpression(filters: {
    searchQuery?: string
    entityFilter?: string
    filterBy?: string
    quickFilter?: QuickFilter
    dateRange?: DateRange
  }): string {
    const quickFilterString = this.buildQuickFilterString(filters.quickFilter)

    const dateFilters = filters.dateRange
      ? `startDate le datetime'${formatDateForFilter(
          filters.dateRange.endDate
        )}' and dueDate ge datetime'${formatDateForFilter(filters.dateRange.startDate)}'`
      : ''

    const searchFilter = filters.searchQuery
      ? `substringof('${filters.searchQuery}',tolower(name)) eq true or substringof('${filters.searchQuery}',tolower(company/name)) eq true`
      : null

    const filterBy = [filters.entityFilter, filters.filterBy, quickFilterString, searchFilter, dateFilters]
      .filter(filter => !!filter)
      .join(' and ')

    return filterBy
  }

  /**
   * Moves project dates by an offset of days
   * @param {string} projectId
   * @param {number} daysOffset
   * @param {{ adjustTasks?: boolean; includeDoneTasks?: boolean; adjustMilestones?: boolean; adjustBookings?: boolean; }} [options={}]
   * @returns {Observable<void>}
   * @param {boolean} options.adjustTasks
   * @param {boolean} options.includeDoneTasks
   * @param {boolean} options.adjustMilestones
   * @param {boolean} options.adjustBookings
   */
  moveDates(
    projectId: string,
    daysOffset: number,
    options: {
      adjustTasks?: boolean
      includeDoneTasks?: boolean
      adjustMilestones?: boolean
      adjustBookings?: boolean
    } = {}
  ): Observable<void> {
    return this.apiClient
      .post<Project>(`${this.url}/${projectId}/adjustDates`, {
        daysOffset,
        ...options
      })
      .pipe(
        map((project: Project) => {
          this.projectStore.update(projectId, project)
        })
      )
  }

  /**
   * Fetches the project's workspaceId
   * @param {string} projectId
   * @returns {Observable<string>}
   */
  fetchWorkspaceId(projectId: string): Observable<string> {
    return this.apiClient
      .get<Workspace>(`${apiEndpoint}/projects/${projectId}/workspace`)
      .pipe(map(workspace => workspace.id))
  }

  /**
   * Returns an api filtering constructed by the quick status filters
   * @param {QuickFilter} quickFilter
   * @returns {string}
   */
  private buildQuickFilterString(quickFilter: QuickFilter): string {
    const currentUser = this.userQuery.getCurrentUser()

    const activeFilter = `(projectStatus/type eq 'not-started' or projectStatus/type eq 'progress')`
    const myActiveFilter = `members/any(p: p/userId eq guid'${currentUser.id}') and ${activeFilter}`
    const closedFilter = `(projectStatus/type eq 'closed' or projectStatus/type eq 'stuck')`

    switch (quickFilter) {
      case 'all':
        return null
      case 'my':
        return myActiveFilter
      case 'progress':
        return activeFilter
      case 'closed':
        return closedFilter
    }
  }

  /**
   * Filters and orders visible columns, including both standard ProjectColumn enum values and custom field columns
   * @param {ProjectColumn[]} visibleColumns
   * @returns {ProjectColumn[]}
   */
  static getOrderedVisibleColumns(visibleColumns: ProjectColumn[]): ProjectColumn[] {
    if (!visibleColumns || visibleColumns.length === 0) {
      return []
    }

    const standardColumns = Object.values(ProjectColumn).filter(column => visibleColumns.includes(column))

    const customFieldColumns = visibleColumns.filter(
      column => !Object.values(ProjectColumn).includes(column as ProjectColumn)
    )

    return [...standardColumns, ...customFieldColumns]
  }

  /**
   * Filters out time tracking columns if time tracking is disabled
   * @param {ProjectColumn[]} visibleColumns
   * @param {boolean} isTimeTrackingDisabled
   * @returns {ProjectColumn[]}
   */
  static filterTimeTrackingColumns(visibleColumns: ProjectColumn[], isTimeTrackingDisabled: boolean): ProjectColumn[] {
    if (!isTimeTrackingDisabled) {
      return visibleColumns
    }

    const timeTrackingColumns = [ProjectColumn.TrackedHours, ProjectColumn.Time]

    return visibleColumns.filter(column => !timeTrackingColumns.includes(column as ProjectColumn))
  }

  /**
   * Extracts custom field IDs from a list of visible columns
   * @param {ProjectColumn[]} visibleColumns
   * @returns {string[]}
   */
  static getCustomFieldIds(visibleColumns: ProjectColumn[]): string[] {
    if (!visibleColumns || visibleColumns.length === 0) {
      return []
    }

    return visibleColumns.filter(column => !Object.values(ProjectColumn).includes(column as ProjectColumn))
  }

  /**
   * Checks if a column is a custom field column
   * @param {ProjectColumn} column
   * @returns {boolean}
   */
  static isCustomFieldColumn(column: ProjectColumn): boolean {
    return !Object.values(ProjectColumn).includes(column as ProjectColumn)
  }
}
